<template>
  <div
    class="message"
    :class="{
      'my-message': message.owner && currentUser.id === message.owner.id,
      'selectable': selectMode
    }"
    @click.capture="captureSelect"
  >
    <div class="message__inner">
      <div class="user-avatar message__avatar" @click="$router.push(`/user/info/${message.owner.id}/personal`)">
        <img v-if="getUserInfo(message.owner.id).avatar !== null && getUserInfo(message.owner.id).avatar.file_size"
             :src="getUserInfo(message.owner.id).avatar.url" :alt="getUserInfo(message.owner.id).full_name">
        <img v-else src="/img/avatar-thumb.png" alt="">
      </div>
      <div class="message__text">
        <div class="message__owner">
          {{ `${getUserInfo(message.owner.id).surname ? `${getUserInfo(message.owner.id).surname} ` : '' }${getUserInfo(message.owner.id).name ? `${getUserInfo(message.owner.id).name}` : '' }` }}
        </div>
        <div v-if="!historyMode && roleAccessControls"
             class="message__menu"
             :class="message.isControlsActive ? 'active' : ''"
             @click.stop="toggleControls"
        >
          <svg width="22" height="3" viewBox="0 0 23 4" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="3" cy="2" r="2" fill="currentColor"/>
            <circle cx="11" cy="2" r="2" fill="currentColor"/>
            <circle cx="19" cy="2" r="2" fill="currentColor"/>
          </svg>
          <transition name="fade">
            <div class="message__popup" v-if="message.isControlsActive">
              <AppPopupMenu :items="roleAccessControlActions"
                            @select-item="selectMessage"
                            @pin-item="pinMessage"
                            @update-item="updateMessage"
                            @reply-item="replyMessage"
                            @remove-item="removeMessage"
                            :usersHaveRead="message.owner.id === currentUser.id ? message.readers : []"
              />
            </div>
          </transition>
        </div>

        <div class="message__reply" v-if="message.quote">
          <div class="reply-to">{{ `${getUserInfo(message.quote.owner.id).surname ? getUserInfo(message.quote.owner.id).surname : '' } ${getUserInfo(message.quote.owner.id).name}` }}</div>
          <div class="message__quote" v-if="message.quote.message">
            <span v-html="message.quote.message.length < 60 ? message.quote.message : `${message.quote.message.slice(0, 60)}...`"></span>
          </div>
        </div>
        <span class="message__text-message plain-html" v-html="getMessageWithLink(message)"></span>
        <div class="message__attachment-preview" v-if="message.attachments.length !== 0 || message.tags.length !== 0">
          <AppMessageAttachmentPreview :files="message.attachments"
                                       :tags="message.tags"
                                       clickable
                                       :is-sending="message.isSending"
          />
        </div>
        <div class="message__info">
          <div class="message__time">{{ currentMessageTime }}</div>
          <transition name="fade" mode="out-in">
            <div class="message__status" v-if="!historyMode && message.status && showStatus">
              <Preloader
                v-if="message.isSending"
                width="14px"
                height="14px"
                is-chat-preloader
              />
              <transition name="fade">
                <svg v-if="message.is_read && !message.isSending" class="message__status-read" width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 5.8L4.14286 9L12 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </transition>
              <svg v-if="!message.isSending" width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5.8L4.14286 9L12 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div v-if="selectMode" :class="['message__select', {active: isSelected}]">
      <svg v-if="isSelected" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M0 11l2-2 5 5L18 3l2 2L7 18z"/></svg>
    </div>
  </div>
</template>

<script>
import AppPopupMenu from '../popup/AppPopupMenu'
import { formatDateTime } from '@/utils/utils'
import AppMessageAttachmentPreview from '@/components/page/input/AppMessageAttachmentPreview'
import Preloader from '@/components/page/Preloader'

export default {
  name: 'DialogMessage',
  components: { Preloader, AppMessageAttachmentPreview, AppPopupMenu },
  props: {
    message: {
      type: Object,
      default: () => {}
    },
    // свойство для отключения некоторых элементов
    historyMode: {
      type: Boolean,
      default: false
    },
    selectMode: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    roleAccessControls () {
      return this.$store.getters.getRoleRightsDialogs.messageActionAccess
    },
    roleAccessControlActions () {
      const actions = this.$store.getters.getRoleRightsDialogs.messageActions
      const isOwner = (this.message.owner && this.message.owner.id === this.currentUser.id)
      return { select: true, pin: isOwner, update: actions.update && isOwner, reply: true, task: false, remove: actions.remove || isOwner }
    },
    showStatus () {
      return this.message.owner && this.message.owner.id === this.currentUser.id
    },
    currentUser () {
      return this.$store.getters.currentUser
    },
    currentMessageTime () {
      const time = this.historyMode ? formatDateTime(this.message.created_at) : this.getTime(this.message.created_at)

      const isUpdated = this.message.created_at !== this.message.updated_at && this.message.status === 2

      return isUpdated ? `${time} (ред. ${formatDateTime(this.message.updated_at)})` : time
    }
  },
  methods: {
    captureSelect (event) {
      if (this.selectMode) {
        this.selectMessage()
        event.stopPropagation()
      }
    },
    toggleControls () {
      this.$emit('toggle-controls', this.message.id)
    },

    convertMessage (item) {
      if (item && item.match('http')) {
        const urlify = function urlify (text) {
          const urlRegex = /((https?|http?):\/\/[^"<\s]+)(?![^<>]*>|[^"]*?<\/a)/g
          return text.replace(urlRegex, function (url) {
            return '<a target="_blank" href="' + url + '">' + url + '</a>'
          })
        }
        const overall = urlify(item)
        return overall
      } else {
        return item || ''
      }
    },
     
    getMessageWithLink (value) {
      if (value.forwarded_messages && value.forwarded_messages.length > 0) {
        let forwarded_messages = ''
        let files = ''
        let forwarded_files = ''
        value.forwarded_messages.map(item => {
          const message = this.convertMessage(item.message)
          item.attachments.forEach(file => {
            files = files + '<a href="' + file.url + '"target="_blank" style="display:flex;align-items:flex-start;margin-bottom:6px;color:black;text-decoration:none"><img src="/img/svg/file.svg" / style="margin-right:4px;"><span>' + file.file_name + '</span></a>'
          })
          forwarded_files = '<div><b>Файлы:</b>' + files + '</div>'
          forwarded_messages = forwarded_messages + '<div><b style="color:#1170B8">' + item.owner.profile.name + '</b> <small>' + item.updated_at + '</small><div>' + message + forwarded_files + '</div></div>'
        })
        return this.convertMessage(value.message) + '<div style="border-left:3px solid #0E619F;padding-left:16px;margin-top:5px">' + forwarded_messages + '</div>'
      } else {
        return this.convertMessage(value.message)
      }
    },
    selectMessage () {
      this.$emit('select-message', this.message)
      this.$emit('toggle-controls', null)
    },
    pinMessage () {
      this.$emit('pin-message', this.message.id)
      // эмитим также событие переключение меню с null чтобы меню закрывалось при нажатии на пункты
      this.$emit('toggle-controls', null)
    },
    updateMessage () {
      this.$emit('update-message', this.message)
      this.$emit('toggle-controls', null)
    },
    replyMessage () {
      const data = {
        id: this.message.id,
        ownerId: this.message.owner.id,
        quote: this.message.message
      }
      this.$emit('reply-message', data)
      this.$emit('toggle-controls', null)
    },
    removeMessage () {
      this.$emit('remove-message', this.message.id)
      this.$emit('toggle-controls', null)
    },
    getTime (date) {
      return formatDateTime(date).slice(-5)
    },
    formatDateTime,

    getUserInfo (id) {
      return this.$store.getters.getFullUserById(id)
    }
  },
  mounted () {}
}

</script>

<style lang="scss" scoped>
@import '../../../assets/css/system/_vars';
@import '../../../assets/css/system/_mixins';

.message {
  margin-right: 30px;
  margin-left: 30px;
  margin-bottom: 12px;
  position: relative;
  transition: padding 0.2s;

  @media screen and (max-width: $desktop-sm_start) {
    margin-right: 20px;
    margin-left: 20px;
  }

  @media screen and (max-width: $tablet_start) {
    margin-right: 14px;
    margin-left: 14px;
  }

  &__inner {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-end;
    margin-bottom: 4px;
  }

  &__text-message {
    word-break: break-word;
  }

  &__avatar {
    position: relative;
    z-index: 2;
    cursor: pointer;
    @include size(36px);
    margin-right: 20px;

    @media screen and (max-width: $tablet_start) {
      @include size(32px);
    }
  }

  &__owner {
    color: $color_second-light;
    margin-bottom: 10px;
  }

  &__text {
    position: relative;
    width: auto;
    min-width: 260px;
    background: $color_sixth;
    font-size: 14px;
    color: $font_color_second;
    border-radius: 5px;
    padding: 12px 40px 20px 14px;
    box-shadow: 0 2px 4px #D0E7F7;

    @media screen and (max-width: $tablet_start) {
      padding: 12px 40px 20px 10px;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: auto;
      left: 100%;
    }

    &::before {
      background: $color_sixth;
      @include size(20px);
      left: -12px;
      bottom: 0;
      box-shadow: -3px 2px 4px #D0E7F7;
    }

    &::after {
      left: -32px;
      bottom: 0;
      width: 30px;
      height: 50px;
      border-radius: 15px;
      background: $color_main;
    }

    &-message {
      white-space: pre-wrap;
    }
  }

  &__reply {
    position: relative;
    margin-bottom: 16px;
    padding-left: 16px;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 3px;
      background: #FAC658;
    }
  }

  &__quote {
    font-size: .8em;
    font-weight: 300;
    font-style: italic;
    color: $font_color_second;
  }

  &__attachment-preview {
    margin-top: 8px;
    font-size: 14px;
  }

  &__menu {
    height: 14px;
    position: absolute;
    top: 6px;
    left: auto;
    right: 12px;
    display: flex;
    align-items: center;
    color: #95A4C0;
    cursor: pointer;
    transition: .3s;

    &:hover {
      color: $color_second-light;
    }

    &.active {
      color: $color_second-light;
    }
  }

  &__popup {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: auto;
    right: calc(100% + 10px);
    z-index: 999;
  }

  &__info {
    position: absolute;
    top: auto;
    left: auto;
    right: 10px;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
    color: $font_color_second;
  }

  &__status {
    position: relative;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    margin-left: 10px;
    color: $color_fourth;

    &-read {
      position: absolute;
      top: 50%;
      left: -6px;
      transform: translateY(-50%);
    }
  }

  &__time {
    white-space: nowrap;
    font-size: 12px;
  }

  .message__select {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 24px;
    max-height: 24px;
    min-width: 24px;
    min-height: 24px;
    border: 1px solid #697178;
    border-radius: 50%;
    padding: 5px;
    position: absolute;
    right: 0;
    top: calc(50% - 12px);
    cursor: pointer;

    &.active {
      background: $font_color_third;
      fill: white;
      border: 1px solid $font_color_third;
    }
  }

  &.selectable {
    padding-right: 40px;
    cursor: pointer;

    .message__inner {
      pointer-events: none;
    }
  }
}

.my-message {
  .message {
    &__inner {
      flex-flow: row-reverse nowrap;
    }

    &__avatar {
      margin-right: 0;
      margin-left: 20px;
    }

    &__text {
      background: #EAF8FE;

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: auto;
        left: 100%;
      }

      &::before {
        background: #EAF8FE;
        @include size(20px);
        left: calc(100% - 8px);
        bottom: 0;
        box-shadow: 3px 2px 4px #D0E7F7;
      }

      &::after {
        left: calc(100% + 2px);
        bottom: 0;
        width: 30px;
        height: 50px;
        border-radius: 15px;
        background: $color_main;
      }
    }
  }
}
</style>
